import { Component, OnInit } from "@angular/core";
import { FormBuilder, Validators } from "@angular/forms";
import { firstValueFrom } from "rxjs";

import { ApiService } from "@bitwarden/common/abstractions/api.service";
import { AccountService } from "@bitwarden/common/auth/abstractions/account.service";
import { TwoFactorProviderType } from "@bitwarden/common/auth/enums/two-factor-provider-type";
import { EmailTokenRequest } from "@bitwarden/common/auth/models/request/email-token.request";
import { EmailRequest } from "@bitwarden/common/auth/models/request/email.request";
import { getUserId } from "@bitwarden/common/auth/services/account.service";
import { PreloginRequest } from "@bitwarden/common/models/request/prelogin.request";
import { I18nService } from "@bitwarden/common/platform/abstractions/i18n.service";
import { MessagingService } from "@bitwarden/common/platform/abstractions/messaging.service";
import { UserId } from "@bitwarden/common/types/guid";
import { ToastService } from "@bitwarden/components";
import { KdfConfigService, KeyService } from "@bitwarden/key-management";

import { SharedModule } from "../../../shared";

@Component({
  selector: "app-change-email",
  templateUrl: "change-email.component.html",
  standalone: true,
  imports: [SharedModule],
})
export class ChangeEmailComponent implements OnInit {
  tokenSent = false;
  showTwoFactorEmailWarning = false;
  userId: UserId | undefined;

  formGroup = this.formBuilder.group({
    step1: this.formBuilder.group({
      masterPassword: ["", [Validators.required]],
      newEmail: ["", [Validators.required, Validators.email]],
    }),
    token: [{ value: "", disabled: true }, [Validators.required]],
  });

  constructor(
    private accountService: AccountService,
    private apiService: ApiService,
    private i18nService: I18nService,
    private keyService: KeyService,
    private messagingService: MessagingService,
    private formBuilder: FormBuilder,
    private kdfConfigService: KdfConfigService,
    private toastService: ToastService,
  ) {}

  async ngOnInit() {
    this.userId = await firstValueFrom(getUserId(this.accountService.activeAccount$));

    const twoFactorProviders = await this.apiService.getTwoFactorProviders();
    this.showTwoFactorEmailWarning = twoFactorProviders.data.some(
      (p) => p.type === TwoFactorProviderType.Email && p.enabled,
    );
  }

  submit = async () => {
    if (this.userId == null) {
      throw new Error("Can't find user");
    }

    // This form has multiple steps, so we need to mark all the groups as touched.
    this.formGroup.controls.step1.markAllAsTouched();

    if (this.tokenSent) {
      this.formGroup.controls.token.markAllAsTouched();
    }

    // Exit if the form is invalid.
    if (this.formGroup.invalid) {
      return;
    }

    const step1Value = this.formGroup.controls.step1.value;
    const newEmail = step1Value.newEmail?.trim().toLowerCase();
    const masterPassword = step1Value.masterPassword;

    if (newEmail == null || masterPassword == null) {
      throw new Error("Missing email or password");
    }

    const currentEmail = (await firstValueFrom(this.accountService.activeAccount$))?.email;
    if (currentEmail == null) {
      throw new Error("Missing current email");
    }
    const kdfConfig = await firstValueFrom(this.kdfConfigService.getKdfConfig$(this.userId));
    if (kdfConfig == null) {
      throw new Error("Missing kdf config");
    }

    // Fyin pepper-strengthened PIN: fold the pepper into every makeMasterKey()
    // input so the derived MK matches what peppered login produces and what was
    // stored at migration time. hashMasterKey() below still uses the raw PIN
    // (matching the PasswordLoginStrategy convention). We deliberately do NOT use
    // getOrDeriveMasterKey here: on a cache miss (e.g. after a lock/unlock cycle,
    // MASTER_KEY clears on lock) it derives the MK from the raw PIN, so the proof
    // hash would not match the server for a migrated user. Deriving explicitly is
    // correct whether or not the key is cached. Pepper is per-user, not per-email,
    // so the same value applies to both the current-email proof and the new key.
    const fyinPepper = await this.fyinFetchPepperIfMigrated(currentEmail);
    const currentPasswordForKdf =
      fyinPepper != null ? masterPassword + fyinPepper : masterPassword;
    const existingMasterKey = await this.keyService.makeMasterKey(
      currentPasswordForKdf,
      currentEmail,
      kdfConfig,
    );
    const existingHash = await this.keyService.hashMasterKey(masterPassword, existingMasterKey);

    if (!this.tokenSent) {
      const request = new EmailTokenRequest();
      request.newEmail = newEmail;
      request.masterPasswordHash = existingHash;
      await this.apiService.postEmailToken(request);
      this.activateStep2();
    } else {
      const token = this.formGroup.value.token;
      if (token == null) {
        throw new Error("Missing token");
      }
      const request = new EmailRequest();
      request.token = token;
      request.newEmail = newEmail;
      request.masterPasswordHash = existingHash;

      // Fyin pepper-strengthened PIN: derive the new master key against the new
      // email with the peppered KDF input so peppered login can re-derive it.
      // kdfConfig + fyinPepper were resolved above (KDF is unchanged by an email
      // change). hashMasterKey still uses the raw PIN.
      const newPasswordForKdf =
        fyinPepper != null ? masterPassword + fyinPepper : masterPassword;
      const newMasterKey = await this.keyService.makeMasterKey(
        newPasswordForKdf,
        newEmail,
        kdfConfig,
      );
      request.newMasterPasswordHash = await this.keyService.hashMasterKey(
        masterPassword,
        newMasterKey,
      );

      const userKey = await firstValueFrom(this.keyService.userKey$(this.userId));
      if (userKey == null) {
        throw new Error("Can't find UserKey");
      }
      const newUserKey = await this.keyService.encryptUserKeyWithMasterKey(newMasterKey, userKey);
      const encryptedUserKey = newUserKey[1]?.encryptedString;
      if (encryptedUserKey == null) {
        throw new Error("Missing Encrypted User Key");
      }
      request.key = encryptedUserKey;

      await this.apiService.postEmail(request);
      this.reset();
      this.toastService.showToast({
        variant: "success",
        title: this.i18nService.t("emailChanged"),
        message: this.i18nService.t("logBackIn"),
      });
      this.messagingService.send("logout");
    }
  };

  /**
   * Fyin pepper-strengthened PIN: returns the pepper string for a migrated user
   * (so callers can derive MK from `pin || pepper` rather than `pin` alone).
   * Returns null for unmigrated users / errors / pepper-disabled servers; callers
   * fall back to the legacy KDF in that case. Pattern matches the existing patch
   * in libs/common/.../user-verification.service.ts.
   */
  private async fyinFetchPepperIfMigrated(email: string): Promise<string | null> {
    try {
      const preloginResponse = await this.apiService.postPrelogin(new PreloginRequest(email));
      if (preloginResponse?.pepperRequired === true && typeof preloginResponse.pepper === "string") {
        return preloginResponse.pepper;
      }
    } catch (e) {
      // 404 / network / etc — fall back to legacy KDF
    }
    return null;
  }

  // Disable step1 and enable token
  activateStep2() {
    this.formGroup.controls.step1.disable();
    this.formGroup.controls.token.enable();

    this.tokenSent = true;
  }

  // Reset form and re-enable step1
  reset() {
    this.formGroup.reset();
    this.formGroup.controls.step1.enable();
    this.formGroup.controls.token.disable();

    this.tokenSent = false;
  }
}
